/**
 * Brand Configuration
 * 
 * Customize terminology, branding, and labels based on the current domain.
 * Each brand can have its own set of terms for AI Agents, projects, etc.
 */

export interface BrandConfig {
  id: string;
  name: string;
  domain: string;
  favicon: string;
  
  // Terminology customization
  terminology: {
    // Singular and plural forms for "AI Agent"
    agent: {
      singular: string;      // e.g., "AI Agent" or "PracticeClaw"
      plural: string;        // e.g., "AI Agents" or "PracticeClaws"
      singularLower: string; // e.g., "ai agent" or "practiceclaw"
      pluralLower: string;   // e.g., "ai agents" or "practicelaws"
    };
    
    // Project terminology
    project: {
      singular: string;
      plural: string;
    };
    
    // Create button text
    createButton: string;    // e.g., "Create AI Agent" or "Create PracticeClaw"
    
    // Navigation and headings
    myAgentsHeading: string; // e.g., "My AI Agents" or "My PracticeClaws"
  };
  
  // SEO and metadata
  metadata: {
    title: string;
    description: string;
  };
}

export const brands: Record<string, BrandConfig> = {
  easygpt: {
    id: 'easygpt',
    name: 'EasyGPT',
    domain: 'easygpt.builders',
    favicon: '/favicon.ico',
    
    terminology: {
      agent: {
        singular: 'AI Agent',
        plural: 'AI Agents',
        singularLower: 'ai agent',
        pluralLower: 'ai agents',
      },
      project: {
        singular: 'Project',
        plural: 'Projects',
      },
      createButton: 'Create AI Agent',
      myAgentsHeading: 'My Inbound GPT Agents',
    },
    
    metadata: {
      title: 'Inbound GPT',
      description: 'Create custom AI agents for your business in minutes. No coding required.',
    },
  },
  
  practiceclaw: {
    id: 'practiceclaw',
    name: 'PracticeClaw',
    domain: 'practiceclaw.com',
    favicon: '/practiceclaw-favicon.svg',
    
    terminology: {
      agent: {
        singular: 'PracticeClaw',
        plural: 'PracticeClaws',
        singularLower: 'practiceclaw',
        pluralLower: 'practicelaws',
      },
      project: {
        singular: 'Practice',
        plural: 'Practices',
      },
      createButton: 'Create PracticeClaw',
      myAgentsHeading: 'My PracticeClaws',
    },
    
    metadata: {
      title: 'PracticeClaw - AI Receptionists for Healthcare Practices',
      description: 'AI-powered receptionists for healthcare practices. Handle calls, book appointments, and manage patient inquiries 24/7.',
    },
  },

  siteglue: {
    id: 'siteglue',
    name: 'SiteGlue',
    domain: 'siteglue.ai',
    favicon: '/siteglue-favicon-32.png',

    terminology: {
      agent: {
        singular: 'Agent',
        plural: 'Agents',
        singularLower: 'agent',
        pluralLower: 'agents',
      },
      project: {
        singular: 'Project',
        plural: 'Projects',
      },
      createButton: 'Create Agent',
      myAgentsHeading: 'My SiteGlue Agents',
    },

    metadata: {
      title: 'SiteGlue',
      description:
        'AI built for humans. Convert conversations into opportunities for your team.',
    },
  },
};

/**
 * Get the current brand configuration based on hostname and optional marketing brand.
 * On app.easygpt.builders, pass marketingBrand "siteglue" when `?brand=siteglue` is present.
 */
export function getCurrentBrand(
  hostname?: string,
  marketingBrand?: "siteglue" | "inbound_gpt" | null,
): BrandConfig {
  // Use provided hostname or get from window if in browser
  const currentHostname = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
  
  // Check which brand matches the current hostname
  if (currentHostname.includes('practiceclaw.com')) {
    return brands.practiceclaw;
  }

  if (currentHostname.includes('siteglue.ai') || marketingBrand === "siteglue") {
    return brands.siteglue!;
  }
  
  // Default to EasyGPT
  return brands.easygpt;
}

/**
 * Hook to get current brand in React components (hostname + ?brand=siteglue / session).
 */
export function useBrand() {
  if (typeof window === 'undefined') {
    return brands.easygpt; // Server-side default
  }

  let marketingBrand: "siteglue" | "inbound_gpt" | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("brand")?.trim().toLowerCase();
    if (fromQuery === "siteglue") {
      marketingBrand = "siteglue";
    } else if (fromQuery === "inbound_gpt" || fromQuery === "easygpt") {
      // Explicit Inbound GPT auth — do not keep a prior SiteGlue twin session.
      marketingBrand = "inbound_gpt";
      sessionStorage.removeItem("eg_marketing_brand");
    } else if (sessionStorage.getItem("eg_marketing_brand") === "siteglue") {
      marketingBrand = "siteglue";
    }
  } catch {
    /* ignore */
  }

  return getCurrentBrand(undefined, marketingBrand);
}
